import { Box, Stack, SxProps, Theme } from "@mui/material";
import { isNil } from "lodash-es";

import { ApiEnumName } from "@/common/services/enum";
import { ContractDto } from "@/core/api/generated/v1";

import InlineApiEnumValue from "../../Enum/InlineApiEnumValue";
import BooleanValue from "../../Form/Display/BooleanValue";
import FieldValue from "../../Form/Display/FieldValue";

export interface OwnProps {
  contract: ContractDto;
  sx?: SxProps<Theme>;
}

export type ContractReminderSettingsDisplayProps = OwnProps;

export default function ContractReminderSettingsDisplay({
  contract,
  sx,
}: ContractReminderSettingsDisplayProps) {
  return (
    <Stack gap={1}>
      <Stack gap={1} direction={{ xxs: "column", sm: "row" }} sx={sx}>
        <FieldValue
          variant='filled'
          direction='column'
          label='Require start date'
          isEmpty={isNil(contract.settings?.isRequireStartDate)}
        >
          <BooleanValue value={contract.settings?.isRequireStartDate} />
        </FieldValue>

        <FieldValue
          variant='filled'
          direction='column'
          label='Require end date'
          isEmpty={isNil(contract.settings?.isRequireEndDate)}
        >
          <BooleanValue value={contract.settings?.isRequireEndDate} />
        </FieldValue>
      </Stack>

      <Box sx={{ width: "max-content" }}>
        <FieldValue
          variant='filled'
          direction='column'
          label='Enable check in/check out'
          isEmpty={isNil(contract.settings?.isCheckEnabled)}
        >
          <BooleanValue value={contract.settings?.isCheckEnabled} />
        </FieldValue>
      </Box>

      {contract.settings?.isCheckEnabled && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xxs: "1fr", md: "max-content max-content" },
            gap: 1,
          }}
        >
          <Stack gap={1}>
            <FieldValue
              variant='filled'
              direction='column'
              label='Require visual inspection before check out'
              isEmpty={isNil(contract.settings?.isRequireVisualInspectionBeforeCheckOut)}
            >
              <BooleanValue value={contract.settings?.isRequireVisualInspectionBeforeCheckOut} />
            </FieldValue>

            <FieldValue
              variant='filled'
              direction='column'
              label='Enable vehicle state acknowledgement on check out'
              isEmpty={isNil(contract.settings?.vehicleStateAckOnCheckOut?.isEnabled)}
            >
              <BooleanValue value={contract.settings?.vehicleStateAckOnCheckOut?.isEnabled} />
            </FieldValue>

            {contract.settings?.vehicleStateAckOnCheckOut?.isEnabled && (
              <>
                <Stack gap={1} direction={{ xxs: "column", sm: "row" }} sx={sx}>
                  <FieldValue
                    variant='filled'
                    direction='column'
                    label='Signature'
                    isEmpty={isNil(contract.settings?.vehicleStateAckOnCheckOut?.signatureReq)}
                  >
                    <InlineApiEnumValue
                      type={ApiEnumName.SubmissionRequirement}
                      value={contract.settings?.vehicleStateAckOnCheckOut?.signatureReq}
                    />
                  </FieldValue>

                  <FieldValue
                    variant='filled'
                    direction='column'
                    label='Personal document(s)'
                    isEmpty={isNil(
                      contract.settings?.vehicleStateAckOnCheckOut?.personalDocumentImageReq,
                    )}
                  >
                    <InlineApiEnumValue
                      type={ApiEnumName.SubmissionRequirement}
                      value={contract.settings?.vehicleStateAckOnCheckOut?.personalDocumentImageReq}
                    />
                  </FieldValue>

                  <FieldValue
                    variant='filled'
                    direction='column'
                    label='Acknowledgement short text'
                    isEmpty={isNil(contract.settings?.vehicleStateAckOnCheckOut?.ackShortText)}
                  >
                    {contract.settings?.vehicleStateAckOnCheckOut?.ackShortText}
                  </FieldValue>
                </Stack>
              </>
            )}
          </Stack>

          <Stack gap={1}>
            <FieldValue
              variant='filled'
              direction='column'
              label='Require visual inspection before check in'
              isEmpty={isNil(contract.settings?.isRequireVisualInspectionBeforeCheckIn)}
            >
              <BooleanValue value={contract.settings?.isRequireVisualInspectionBeforeCheckIn} />
            </FieldValue>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
